import type { DocumentMetadata } from '@skop-app/lib-domain/types'

export type OrganizationMetadataEntry = {
  key: string
  type: string
  value: unknown
}

export type OrganizationDocumentValue = {
  default: boolean
  id: string
  link: string
  name: string
}

export type OrganizationDocumentEntry = DocumentMetadata & {
  value: OrganizationDocumentValue
}

export const isOrganizationMetadataEntry = (
  value: unknown,
): value is OrganizationMetadataEntry => {
  if (typeof value !== 'object' || value === null) return false

  const record = value as Record<string, unknown>
  return (
    typeof record.key === 'string' &&
    typeof record.type === 'string' &&
    'value' in record
  )
}

export const isOrganizationMetadataEntryArray = (
  value: unknown,
): value is OrganizationMetadataEntry[] =>
  Array.isArray(value) && value.every(isOrganizationMetadataEntry)

const isOrganizationDocumentValue = (
  value: unknown,
): value is OrganizationDocumentValue => {
  if (typeof value !== 'object' || value === null) return false

  const record = value as Record<string, unknown>
  return (
    typeof record.id === 'string' &&
    typeof record.name === 'string' &&
    typeof record.link === 'string' &&
    typeof record.default === 'boolean'
  )
}

export const getOrganizationDocumentEntries = (
  metadata: OrganizationMetadataEntry[],
): OrganizationDocumentEntry[] =>
  metadata.filter(
    (entry): entry is OrganizationDocumentEntry =>
      entry.key === 'DOCUMENT' && isOrganizationDocumentValue(entry.value),
  )

export const readMetadataBoolean = (
  metadata: OrganizationMetadataEntry[],
  key: string,
) => {
  const entry = metadata.find((item) => item.key === key)
  return entry?.value === true || entry?.value === 'true'
}

export const serializeMetadataValue = (value: unknown): string => {
  return typeof value === 'string' ? value : JSON.stringify(value ?? null)
}

export const serializeOrganizationMetadata = <
  T extends OrganizationMetadataEntry,
>(
  metadata: T[],
) =>
  metadata.map((entry) => ({
    ...entry,
    value: serializeMetadataValue(entry.value),
  }))
